export type InternationalIdSeeds = Record<string, string>

export function corridorKey(originIso: string | null | undefined, destinationIso: string | null | undefined): string | null {
  if (!originIso || !destinationIso || originIso === destinationIso) return null
  return `${originIso.toUpperCase()}-${destinationIso.toUpperCase()}`
}

export function parseSeed(seed: string): { operationalPrefix: string; suffixStart: number; length: number } | null {
  const normalized = seed.trim().toUpperCase()
  const match = /^([A-Z]+)(\d+)$/.exec(normalized)
  if (!match) return null
  const numeric = match[2]!
  const trailingZeros = numeric.match(/0+$/)?.[0].length ?? 0
  if (trailingZeros === 0) return null
  const suffixStart = numeric.length - trailingZeros
  return {
    operationalPrefix: `${match[1]}${numeric.slice(0, suffixStart)}`,
    suffixStart,
    length: numeric.length,
  }
}

export function validateInternationalId(value: string, seed: string): boolean {
  const parsed = parseSeed(seed)
  if (!parsed) return false
  const normalized = value.trim().toUpperCase()
  const suffix = normalized.slice(parsed.operationalPrefix.length)
  return normalized.startsWith(parsed.operationalPrefix)
    && suffix.length === parsed.length - parsed.suffixStart
    && /^\d+$/.test(suffix)
}

export function nextInternationalId(seed: string, existing: string[]): string | null {
  const parsed = parseSeed(seed)
  if (!parsed) return null
  const suffixLength = parsed.length - parsed.suffixStart
  const maxSuffix = 10n ** BigInt(suffixLength) - 1n
  const values = existing
    .map(value => value.trim().toUpperCase())
    .filter(value => validateInternationalId(value, seed))
    .map(value => BigInt(value.slice(parsed.operationalPrefix.length)))
  const seedSuffix = BigInt(seed.slice(parsed.operationalPrefix.length))
  const maximum = values.reduce((max, value) => value > max ? value : max, seedSuffix)
  const next = values.length > 0 ? maximum + 1n : maximum
  if (next > maxSuffix) return null
  return `${parsed.operationalPrefix}${next.toString().padStart(suffixLength, "0")}`
}
